import { useMemo } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  History as HistoryIcon,
  Pencil,
  SquareStack,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { FieldMeta, ResourceMeta } from '../../types/resource';
import type { PaginatedSubmissions, SubmissionRow } from '../../types/submission';
import { formatFieldValue } from '../../utils/fieldValue';

interface ResourceGridProps {
  meta: ResourceMeta;
  /** Parent resource name — echoed to the detail-drawer callback. */
  parentResource: string;
  page: PaginatedSubmissions | null;
  loading: boolean;
  sort?: string;
  sortOrder?: 'asc' | 'desc';
  onSort: (column: string) => void;
  onEditRow?: (row: SubmissionRow) => void;
  onHistoryRow?: (row: SubmissionRow) => void;
  /**
   * Fired when the reviewer clicks the sub-records action. The parent
   * page opens a `ResourceDetailDrawer` for the row — used instead of
   * the older inline-expand pattern so nested child tables don't
   * inherit the parent grid's horizontal width.
   */
  onViewRow?: (row: SubmissionRow) => void;
}

/**
 * Read-only data grid driven by the resource meta.
 *
 * Row actions (right-aligned):
 *   - History  (always when the callback is provided)
 *   - Edit     (always when the callback is provided)
 *   - View sub-records (only when the meta declares `children[]` and
 *     `onViewRow` is provided)
 *
 * Inline row-expansion was removed here — nesting the children under
 * a `colSpan={totalColumns}` cell inherited the parent grid's
 * horizontal width, which with 15+ columns became a scrolling
 * nightmare. Children now open in a right-side drawer whose width is
 * bounded regardless of how many columns the parent has (see
 * `ResourceDetailDrawer`).
 */
export function ResourceGrid({
  meta,
  parentResource: _parentResource,
  page,
  loading,
  sort,
  sortOrder,
  onSort,
  onEditRow,
  onHistoryRow,
  onViewRow,
}: ResourceGridProps) {
  const columns = useMemo(() => visibleColumns(meta), [meta]);
  const sortable = useMemo(() => sortableColumnTitles(meta), [meta]);
  // The View button now opens a full-record drawer that also carries
  // any sub-records, so we surface it whenever a callback is wired —
  // no longer conditioned on `children[]`. Reviewers need this action
  // to see the hidden-by-curation columns (Owned Land, Survey Season,
  // and so on) that no longer appear in the header row.
  const canView = Boolean(onViewRow);
  const hasRowActions = Boolean(onEditRow || onHistoryRow || canView);

  const rows = page?.data ?? [];
  const totalColumns = columns.length + (hasRowActions ? 1 : 0);

  return (
    <div className="relative overflow-x-auto rounded-xl border border-rgf-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-rgf-gray-200 text-sm">
        <thead className="bg-rgf-gray-50">
          <tr>
            {columns.map((col) => {
              const isSortable = sortable.has(col.title);
              const isActive = sort === col.title;
              return (
                <th
                  key={col.title}
                  scope="col"
                  className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-rgf-gray-600"
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.title)}
                      className={clsx(
                        'inline-flex items-center gap-1 rounded hover:text-rgf-gray-900',
                        isActive && 'text-rgf-gray-900',
                      )}
                    >
                      <span>{col.display_title || col.title}</span>
                      <SortIndicator active={isActive} order={sortOrder} />
                    </button>
                  ) : (
                    <span>{col.display_title || col.title}</span>
                  )}
                </th>
              );
            })}
            {hasRowActions && (
              <th
                scope="col"
                className="sticky right-0 z-10 w-32 whitespace-nowrap bg-rgf-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-rgf-gray-600"
              >
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-rgf-gray-100 bg-white">
          {loading && rows.length === 0 && <SkeletonRows columns={totalColumns} />}
          {!loading && rows.length === 0 && (
            <tr>
              <td
                colSpan={totalColumns}
                className="px-4 py-12 text-center text-sm text-rgf-gray-500"
              >
                No submissions match the current filters.
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr
              key={String(row.id ?? Math.random())}
              className="hover:bg-rgf-gray-50"
            >
              {columns.map((col) => (
                <td
                  key={col.title}
                  className="whitespace-nowrap px-4 py-3 text-rgf-gray-800"
                >
                  {formatFieldValue(col, row[col.title])}
                </td>
              ))}
              {hasRowActions && (
                <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-3 text-right group-hover:bg-rgf-gray-50">
                  <div className="inline-flex items-center gap-1">
                    {canView && (
                      <button
                        type="button"
                        onClick={() => onViewRow!(row)}
                        className="rounded p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-primary-700"
                        title="View details"
                      >
                        <SquareStack className="h-4 w-4" />
                      </button>
                    )}
                    {onHistoryRow && (
                      <button
                        type="button"
                        onClick={() => onHistoryRow(row)}
                        className="rounded p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
                        title="View change history"
                      >
                        <HistoryIcon className="h-4 w-4" />
                      </button>
                    )}
                    {onEditRow && (
                      <button
                        type="button"
                        onClick={() => onEditRow(row)}
                        className="rounded p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-primary-700"
                        title="Edit row"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length > 0 && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 animate-pulse bg-rgf-primary-500" />
      )}
    </div>
  );
}

function SortIndicator({ active, order }: { active: boolean; order?: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />;
  return order === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />;
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((r) => (
        <tr key={r}>
          {Array.from({ length: columns }).map((_, c) => (
            <td key={c} className="px-4 py-3">
              <div className="h-3 w-3/4 animate-pulse rounded bg-rgf-gray-200" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Columns shown in the grid — a **curated** set derived from
 * `meta.view_meta.summary_view` (id + title + subtitle + summary[]).
 * This keeps the grid readable on resources like `outreach_survey`
 * that carry 40+ list-visible fields; the reviewer sees only the
 * identity + at-a-glance columns and clicks "View details" to see
 * everything in the drawer.
 *
 * Falls back to the old "all list_visible fields" set when the meta
 * doesn't declare a summary_view, so resources that never bothered
 * to map summary keys keep behaving as before.
 */
function visibleColumns(meta: ResourceMeta): FieldMeta[] {
  const fields = meta.view_meta?.fields ?? [];
  const summaryView = meta.view_meta?.summary_view;

  if (summaryView) {
    const orderedTitles: string[] = [];
    const push = (t: string | undefined | null) => {
      if (typeof t !== 'string' || t === '') return;
      if (orderedTitles.includes(t)) return;
      orderedTitles.push(t);
    };
    push(summaryView.id);
    push(summaryView.title);
    push(summaryView.subtitle);
    for (const s of summaryView.summary ?? []) push(s);
    if (orderedTitles.length > 0) {
      const byTitle = new Map(fields.map((f) => [f.title, f]));
      return orderedTitles
        .map((t) => byTitle.get(t))
        .filter((f): f is FieldMeta => Boolean(f));
    }
  }
  return fields.filter((f) => f.list_visible !== false);
}

/**
 * Set of field titles the meta declares as sortable — the wire shape
 * for `sort.columns` is a bare `string[]`. A field is sortable iff its
 * title is in that list.
 */
function sortableColumnTitles(meta: ResourceMeta): Set<string> {
  return new Set(meta.view_meta?.sort?.columns ?? []);
}
